'use client';
// `window` exists in browser tabs; Web Worker scopes (including extension
// service workers) expose `importScripts` on `globalThis`. If neither is
// present we are in Node.js and this module should not have been imported.
if (typeof window === 'undefined' && !('importScripts' in globalThis) && !process.env.VITEST) {
    throw new Error('pplx/frontend/metrics/browser must only be imported in browser environments. ' +
        'Use pplx/frontend/metrics/node for server-side code.');
}
import { redactUrlQueryData } from '#pplx/frontend/common/redactAuthParams.js';
import { isSupportedBrowser } from '#pplx/frontend/common/supportedBrowsers.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { startTimer as coreStartTimer, METRIC_NAME } from '#pplx/frontend/metrics/internal/core.js';
import { datadogRum, DEFAULT_TRACKED_RESOURCE_HEADERS } from '@datadog/browser-rum';
function getTrackedResourceHeaders() {
    return [
        ...DEFAULT_TRACKED_RESOURCE_HEADERS.map((name) => ({ name })),
        { name: 'cf-mitigated', location: 'response' },
        { name: 'cf-ray', location: 'response' },
    ];
}
function redactRumUrl(url) {
    return redactUrlQueryData(url).replace(/(\/web\/direct-files\/).*/s, '$1redacted');
}
function redactRumEventUrls(event) {
    event.view.url = redactRumUrl(event.view.url);
    if (event.view.referrer) {
        event.view.referrer = redactRumUrl(event.view.referrer);
    }
    if (event.type === 'resource') {
        event.resource.url = redactRumUrl(event.resource.url);
    }
    if (event.type === 'view') {
        const lcp = event.view.performance?.lcp;
        if (lcp?.resource_url) {
            lcp.resource_url = redactRumUrl(lcp.resource_url);
        }
    }
    if (event.type !== 'error') {
        return;
    }
    const errorResource = event.error.resource;
    if (!errorResource?.url) {
        return;
    }
    const rawResourceUrl = errorResource.url;
    const redactedResourceUrl = redactRumUrl(rawResourceUrl);
    errorResource.url = redactedResourceUrl;
    if (typeof event.error.message === 'string') {
        event.error.message = event.error.message.split(rawResourceUrl).join(redactedResourceUrl);
    }
}
export function getUseServiceWorker() {
    return (typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        !!navigator.serviceWorker.controller);
}
/**
 * Report metrics to the server. This is fire-and-forget and does not wait for a response.
 * @param data data to send
 * @returns
 */
export function reportMetric(metricName, { 
// Ignore these options
verbose, raiseOnError, onlyUseRum = true, 
// Include these options
...data }) {
    const { duration, startTime, ...rest } = data;
    performance.mark(metricName);
    datadogRum.addDurationVital(metricName, {
        startTime,
        duration,
        context: rest,
    });
    if (!onlyUseRum) {
        logger.info(METRIC_NAME, {
            metricName,
            ...data,
        });
    }
}
/**
 * Start a timer for a metric
 * @param extra - Extra labels for the metric
 * @returns A timer object
 */
export function startTimer(extra = {}, config = {}) {
    const startTime = (config.nowFromTimeOrigin ?? performance.now()) + getTimeOrigin();
    return coreStartTimer(extra, {
        ...config,
        transport: (metricName, duration, extra) => {
            performance.mark(metricName);
            datadogRum.addDurationVital(metricName, {
                startTime,
                duration,
                context: extra,
            });
        },
        getTimeOrigin,
    });
}
/**
 * Set global context for the metrics
 * @param context context
 */
export function setGlobalContext(context) {
    Object.entries(context).forEach(([key, value]) => {
        datadogRum.setGlobalContextProperty(key, value);
    });
}
/**
 * Set view-scoped context on the current RUM view. Unlike `setGlobalContext`,
 * properties set here apply only to events emitted from the current view and
 * reset when the user navigates to another route (a new view in DD RUM).
 * Use this for per-page/per-thread tagging.
 * @param context context
 */
export function setViewContext(context) {
    Object.entries(context).forEach(([key, value]) => {
        datadogRum.setViewContextProperty(key, value);
    });
}
/**
 * Report an action to the server. This is fire-and-forget and does not wait for a response.
 * @param actionName - The name of the action
 * @param extra - Extra labels for the action
 */
export function reportAction(actionName, extra = {}) {
    datadogRum.addAction(actionName, extra);
}
/**
 * Send a handled failure to RUM Error Tracking, which groups it and gives it a
 * stack — unlike a custom action, which has neither. Use this for errors the app
 * catches and turns into UI state (failed fetches, dropped websockets, render
 * boundaries); unhandled exceptions are captured automatically.
 *
 * `context` is retained and queryable, so it must never carry secrets, request
 * bodies, or user content.
 */
export function reportError(error, context = {}) {
    datadogRum.addError(error, context);
}
/**
 * Start a named RUM view. Needed by apps with no router, where RUM would
 * otherwise attribute the whole session to a single view — making view counts,
 * time-on-view, and funnels unavailable. Call it on each top-level surface
 * change; RUM closes the previous view automatically.
 */
export function startView(name) {
    datadogRum.startView({ name });
}
/**
 * Associate the current RUM session with a user so events carry `@usr.*`.
 * Thin passthrough to datadogRum.setUser; safe to call before init (buffered).
 */
export function setUser(user) {
    datadogRum.setUser(user);
}
export function reportCustomMetricViaLog(metricName, extra = {}) {
    logger.info(METRIC_NAME, {
        metricName,
        ...extra,
    });
}
export function getTimeOrigin() {
    return performance.timeOrigin;
}
export function getRumSessionId() {
    try {
        return datadogRum.getInternalContext()?.session_id;
    }
    catch {
        return undefined;
    }
}
/**
 * Known browser extension error message patterns — not actionable.
 * Extensions inject code into pages and throw errors our instrumentation captures.
 */
const EXTENSION_ERROR_PATTERNS = [
    'runtime.sendMessage', // Chrome/browser extension runtime API (e.g. "Invalid call to runtime.sendMessage()")
    'runtime.connect', // Extension port connections
    'Extension context invalidated', // Extension updated/disabled mid-session
    'Receiving end does not exist', // Extension messaging failure
    'react-render-tracker', // React Render Tracker extension
    'A listener indicated an asynchronous response', // Extension message listener
    '_AutofillCallbackHandler', // Instagram/Facebook in-app WebView autofill callback
];
const EXTENSION_PROTOCOL_RE = /(?:chrome|moz|safari(?:-web)?)-extension:\/\//;
function isExtensionError(message, stack) {
    if (message && EXTENSION_ERROR_PATTERNS.some((p) => message.includes(p))) {
        return true;
    }
    if (message?.includes('el2.hide') && stack?.includes('removeElements')) {
        return true;
    }
    return !!stack && EXTENSION_PROTOCOL_RE.test(stack);
}
/**
 * Initialize global metrics agent
 * @param options options for the metrics agent
 */
export function initialize(options) {
    if (!options) {
        return;
    }
    const browserSupported = isSupportedBrowser(navigator.userAgent);
    const { applicationId, clientToken, version, env, debug, service, allowedTracingUrls, trackUserInteractions, traceSampleRate, } = options;
    // Only initialize if we have enough information (mostly non-local)
    if (!applicationId || !clientToken) {
        return;
    }
    datadogRum.init({
        applicationId,
        clientToken,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service: service ?? 'web-nextjs',
        env,
        version,
        sessionSampleRate: options.sessionSampleRate,
        sessionReplaySampleRate: options.sessionReplaySampleRate,
        // Percentage, not a fraction: this default is 1%, which is why RUM↔APM
        // correlation only resolves for one request in a hundred unless an app
        // raises it.
        traceSampleRate: traceSampleRate ?? 1,
        // Datadog defaults this to true. Left undefined here so existing apps keep
        // that default; apps rendering especially sensitive clickable DOM can opt
        // out as defense in depth. See InitializeOptions.trackUserInteractions.
        ...(trackUserInteractions === undefined ? {} : { trackUserInteractions }),
        enablePrivacyForActionName: true,
        defaultPrivacyLevel: 'mask',
        trackResourceHeaders: getTrackedResourceHeaders(),
        allowedTracingUrls: allowedTracingUrls ?? [/\.perplexity\.ai/, 'http://localhost:3000'],
        compressIntakeRequests: true,
        silentMultipleInit: debug,
        beforeSend: (event, context) => {
            redactRumEventUrls(event);
            // Old browsers throw unactionable errors, which is what this drop is for.
            // It used to discard *every* event type, which silently removed those
            // users from session, view, and action data too — so usage counts were
            // undercounted by an amount that could not even be estimated, since the
            // drop leaves no trace. Keep the errors out; keep the usage.
            // `browserSupported` is also exposed as global context so the affected
            // population is a queryable segment rather than a blind spot.
            if (!browserSupported && event.type === 'error') {
                return false;
            }
            const errorMessage = event.type === 'error' && typeof event.error.message === 'string'
                ? event.error.message
                : undefined;
            // External SDK/service-worker heartbeat timeout — not actionable. The type
            // says that `message` is supposed to be a string, but it's sometimes an `object`
            if (errorMessage?.includes('Provider unavailable')) {
                return false;
            }
            // Safari throws "Importing a module script failed" when loading stale cached
            // JS that references chunks removed by a new deployment. Already handled by
            // Dynamic import retry + critical reload; suppress from Error Tracking.
            if (errorMessage?.includes('Importing a module script failed')) {
                return false;
            }
            // Browser extension errors — not actionable
            if (event.type === 'error' && isExtensionError(errorMessage, event.error.stack)) {
                return false;
            }
            if (options.beforeSend) {
                return options.beforeSend(event, context);
            }
            return true;
        },
    });
    setGlobalContext({
        useServiceWorker: getUseServiceWorker(),
        browserSupported,
    });
    if (debug) {
        // @ts-expect-error private api
        datadogRum._setDebug(true);
    }
}
//# sourceMappingURL=browser.js.map